<template>
  <section id="contact" class="py-2 light-section">
    <div class="container text-center">
      <div class="elbow-layout">

        <!-- Start label -->
        <div class="label start">{{ startLabel }}</div>

        <!-- List rendered dynamically -->
        <div class="list">
          <div
              v-for="(item, index) in contactItems"
              :key="index"
              :class="['item', getItemPosition(index)]"
          >
            <div class="left-elbow elbow"></div>
            <div class="text">
              <div class="text-box">{{ item }}</div>
            </div>
            <div class="right-elbow elbow"></div>
          </div>
        </div>

        <!-- End button -->
        <div class="button-wrapper">
          <div class="button-container">
            <a class="btn btn-primary btn-lg mt-2" @click="$router.push('/contact')">
              Kontaktieren Sie uns
              <i class="fas fa-envelope"></i>
            </a>
          </div>
        </div>

      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'ContactSection',

  data() {
    return {
      startLabel: 'Sie haben...',
      contactItems: [
        '...ein Problem?',
        '...eine Idee?',
        '...Interesse an Veränderung?',
        '...eine Vision?',
        '...ein konkretes Projekt?',
        '...etwas Außergewöhnliches vor?',
      ]
    };
  },

  methods: {
    getItemPosition(index: number): string {
      if (index === 0) return 'top';
      if (index === this.contactItems.length - 1) return 'bottom';
      return 'middle';
    }
  }
});
</script>

<style scoped lang="less">
@import '@/assets/style.less';

.elbow-layout {
  display: flex;
  align-items: center;  /* vertical center everything */
  justify-content: center; /* horizontal center */
  font-family: sans-serif;
  padding: 2rem;
}

/* Shared label base */
.label {
  position: relative;
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  white-space: nowrap;
  margin: 0 1rem;
}

/* Default connector line on the right */
.label::after {
  content: "";
  position: absolute;
  top: 50%;
  right: -1rem;
  width: 12px;
  height: 2px;
  background: @primary-color;
  transform: translateY(-50%);
}

/* List structure */
.list {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

/* Vertical connectors on left */
.item::before {
  content: "";
  position: absolute;
  left: 0;
  width: 2px;
  background: @primary-color;
}

/* Vertical connectors on right */
.item::after {
  content: "";
  position: absolute;
  right: 0;
  width: 2px;
  background: @primary-color;
}

/* Top item: connector goes down from center */
.item.top::before,
.item.top::after {
  top: 50%;
  height: 50%;
}

/* Middle items: full height connector */
.item.middle::before,
.item.middle::after {
  top: 0;
  height: 100%;
}

/* Bottom item: connector goes up to center */
.item.bottom::before,
.item.bottom::after {
  top: 0;
  height: 50%;
}

/* Fix .text min-width to ensure elbows have room */
.text {
  flex: 1 1 0;       /* grow and shrink, no base width */
  min-width: 0;      /* allow shrinking below content size */
  text-align: center;
  font-size: 1.2rem;
  white-space: nowrap;  /* allow wrapping */
  margin-bottom: 0.1rem;
}

/* Rounded fixed-width boxes around text */
.text-box {
  width: 100%;       /* fill the flex parent */
  padding: 0 1rem;
  border: 2px solid #666666;
  border-radius: 12px;
  box-sizing: border-box;
  text-align: center;
  font-size: 1.2rem;
  user-select: none;
}

/* Fix elbows so they never shrink */
.elbow {
  width: 40px;
  flex-shrink: 0;   /* don't shrink */
  position: relative;
  background: white;
  z-index: 1;
}

/* Horizontal elbow branch */
.elbow::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  height: 2px;
  width: 100%;
  background: @primary-color;
  transform: translateY(-50%);
}

/* Right elbow is mirrored */
.right-elbow {
  transform: scaleX(-1);
}

.button-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  margin-left: 1rem;
  white-space: normal;
}

/* Container to vertically center the button and the elbow */
.button-container {
  position: relative;
  display: flex;
  align-items: center;  /* vertically center button and elbow */
  padding-left: 5px;
  line-height: 1;       /* tighten line height */
}

.button-container a.btn {
  transform: translateY(-3px); /* nudge upwards */
}

/* Horizontal elbow connector */
.button-container::after {
  content: "";
  position: absolute;
  left: -1rem;  /* elbow width */
  top: 50%;
  width: 20px;
  height: 2px;
  background: @primary-color;
  transform: translateY(-50%);
}

@media (max-width: 600px) {
  .elbow-layout {
    flex-direction: column;
    align-items: center;
    padding: 0rem;
    justify-items: center;
  }

  .label {
    margin: 0 0 1rem 0;
    font-size: 1.3rem;
    white-space: normal;
    justify-content: center;
  }

  .label::after {
    display: none; /* hide connector line */
  }

  .list {
    width: 100%;
  }

  .item {
    flex-direction: row;
    justify-content: center;
    position: static;
    margin-bottom: .5rem;
  }

  /* Hide elbows and vertical connectors */
  .elbow,
  .item::before,
  .item::after {
    display: none;
  }

  .text {
    flex: 1 1 auto;
    min-width: auto;
    max-width: 90%;
    white-space: nowrap;
    margin-bottom: 0;
    justify-items: center;
  }

  .text-box {
    max-width: 80%;
    border-radius: 12px;
    padding: 0.2rem .5rem;
    font-size: 1rem;
  }

  .button-wrapper {
    margin-left: 0;
    margin-top: .5rem;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .button-container {
    justify-content: center;
  }

  .button-container::after {
    display: none;
  }
}
</style>
